import { useDeleteReports, useUpdateReports } from "@/api/useReports"
import { Flex, Text } from "@/atoms"
import { SAVING_CHANGES_PROPS, useLoadingMessage } from "@/hooks/useMessage"
import { useQueryString } from "@/hooks/useQueryString"
import { useTooltip } from "@/hooks/useTooltip"
import { useTranslation } from "@/hooks/useTranslation"
import { useUser } from "@/hooks/useUser"
import { DeleteConfirmModal, useConfirmModal } from "@/molecules/ConfirmModal"
import { CreateActionEmptyTable } from "@/molecules/EmptyTable"
import { Protected } from "@/molecules/Protected"
import { useReportsDefinitions } from "@/pages/ReportsPage/applications/useReports"
import { Filter } from "@/pages/SettingsPage/components/organisms/Filter"
import { TabTemplate } from "@/templates/TabTemplate"
import { getColumnsTable } from "./getColumnsTable"

import type { ReportDefinition } from "@/domain/report"
import type { TabProps } from "@/pages/ReportsPage"

export const RecurringReportsTab = (props: TabProps) => {
  const { t } = useTranslation()
  const params = useQueryString()
  const [{ response, isLoading }] = useReportsDefinitions(params)
  const { data, metadata } = response || {}
  const remover = useDeleteReports()
  const { confirm: confirmModal } = useConfirmModal()
  const updater = useUpdateReports()
  const { permissions, createURL } = props
  const { hasPermission } = useUser()
  const noPermissions = !hasPermission(permissions)

  const statusChange = useLoadingMessage(
    SAVING_CHANGES_PROPS,
    (isEnabled: boolean, { id }: ReportDefinition) =>
      updater.mutateAsync({
        needle: `/${id}/enabled`,
        data: { id, isEnabled }
      })
  )
  const onDelete = (record: ReportDefinition) => {
    const { id, name } = record

    confirmModal((props) =>
      DeleteConfirmModal({
        name,
        entityName: "report",
        onOk: async () => await remover.mutateAsync(id),
        ...props
      })
    )
  }
  const columns = getColumnsTable({
    t,
    onDelete,
    statusChange,
    noPermissions,
    ...props
  })

  useTooltip(data)

  return (
    <TabTemplate
      resizable
      headerComponent={
        <Flex>
          <Flex flex="780px 0 0">
            <Text type="secondary">{t("reports.manage.description")}</Text>
          </Flex>
        </Flex>
      }
      SearchComponent={<Filter />}
      columns={columns}
      loading={isLoading}
      dataSource={data}
      metadata={metadata}
      locale={{
        customEmptyComponent: (
          <Protected shouldDisable permission={permissions}>
            <CreateActionEmptyTable tKey="report" to={createURL} />
          </Protected>
        )
      }}
      onRowClick={props.onEdit}
    />
  )
}
